/**
 * Maps backend API error messages and validation error strings to i18n keys
 * so the UI can show bilingual, user-friendly messages.
 */

export type TranslateFunction = (key: string, options?: Record<string, unknown>) => string;

/** Known backend message substrings -> errors.api.* key */
const API_MESSAGE_MAP: Array<{ pattern: string | RegExp; key: string }> = [
  { pattern: 'NETWORK_ERROR', key: 'errors.api.networkError' },
  { pattern: /accept the Code of Ethics|Code of Ethics/i, key: 'errors.api.ethicsRequired' },
  { pattern: /GDPR|privacy policy/i, key: 'errors.api.gdprRequired' },
  { pattern: /transfer of personal data|International Committees to register/i, key: 'errors.api.dataTransferRequired' },
  { pattern: /membership statement/i, key: 'errors.api.statementRequired' },
  { pattern: /All three representatives|valid International Committee/i, key: 'errors.api.committeesRequired' },
  { pattern: /At least one institution name/i, key: 'errors.api.institutionNameRequired' },
  { pattern: /First name and last name are required/i, key: 'errors.api.nameRequired' },
  { pattern: /Institution not found/i, key: 'errors.api.institutionNotFound' },
  { pattern: /Institution verification failed|check institution name/i, key: 'errors.api.institutionVerificationFailed' },
  { pattern: /institution already has a user account|This institution already has/i, key: 'errors.api.institutionAlreadyHasAccount' },
  { pattern: /Existing institution registration is only available/i, key: 'errors.api.institutionRoleRequired' },
  { pattern: /Existing member registration is only available/i, key: 'errors.api.memberRoleRequired' },
  { pattern: /email is already registered to another/i, key: 'errors.api.emailAlreadyInUse' },
  { pattern: /already has a user account|member already has/i, key: 'errors.api.memberAlreadyHasAccount' },
  { pattern: /Member verification failed|check first name, last name/i, key: 'errors.api.memberVerificationFailed' },
  { pattern: /Member not found/i, key: 'errors.api.memberNotFound' },
  { pattern: /already exists|already registered/i, key: 'errors.api.emailExists' },
  { pattern: /Invalid or expired|expired.*token/i, key: 'errors.api.invalidToken' },
  { pattern: /Password is required/i, key: 'validation.passwordRequired' },
  { pattern: /Password must be at least/i, key: 'validation.passwordMinLength' },
  { pattern: /Professional phone/i, key: 'validation.phoneInvalid' },
  { pattern: /number is invalid \(expected 8/i, key: 'validation.phoneInvalid' },
  { pattern: /Professional email/i, key: 'validation.emailInvalid' },
  { pattern: /address is invalid/i, key: 'validation.emailInvalid' },
  { pattern: /cannot be before start year/i, key: 'register.studies.yearsOutOfOrder' },
  { pattern: /Study start year/i, key: 'register.studies.startYearInvalid' },
  { pattern: /Study completion year/i, key: 'register.studies.completionYearInvalid' },
  { pattern: /study duration must be between/i, key: 'register.studies.durationInvalid' },
  { pattern: /Website must be a valid/i, key: 'profile.validation.invalidUrl' },
  { pattern: /card replacement petition is already pending/i, key: 'petitions.pendingReplacementExists' },
];

/** Login-specific backend message substrings -> login.errors.* key */
const LOGIN_MESSAGE_MAP: Array<{ pattern: string | RegExp; key: string }> = [
  { pattern: 'NETWORK_ERROR', key: 'login.errors.networkError' },
  { pattern: /Invalid email or password|invalid.*credentials/i, key: 'login.errors.invalidCredentials' },
  { pattern: /confirm your email|email.*confirm|Please confirm/i, key: 'login.errors.emailNotConfirmed' },
  { pattern: /account.*locked|locked/i, key: 'login.errors.accountLocked' },
  { pattern: /Too many|too many attempts/i, key: 'login.errors.tooManyAttempts' },
];

/**
 * Returns a translated message for login errors. Use in Login page catch block.
 * Maps known backend messages (including NETWORK_ERROR from auth service) to login.errors.* keys.
 */
export function getLoginErrorMessage(backendMessage: string, t: TranslateFunction): string {
  if (!backendMessage?.trim()) {
    return t('login.errors.generic');
  }
  for (const { pattern, key } of LOGIN_MESSAGE_MAP) {
    if (typeof pattern === 'string' && backendMessage.includes(pattern)) {
      return t(key);
    }
    if (pattern instanceof RegExp && pattern.test(backendMessage)) {
      return t(key);
    }
  }
  return t('login.errors.generic');
}

/**
 * Returns a user-facing message for an API error by mapping known backend
 * messages to i18n keys. Use for Register and Edit catch blocks.
 * When registration: true, never returns raw English backend text.
 */
export function getApiErrorMessage(
  backendMessage: string,
  t: TranslateFunction,
  options?: { registration?: boolean; profile?: boolean }
): string {
  if (!backendMessage?.trim()) {
    if (options?.profile) return t('errors.api.profileUpdateFailed');
    if (options?.registration) return t('errors.api.registrationFailed');
    return t('errors.api.generic');
  }
  for (const { pattern, key } of API_MESSAGE_MAP) {
    if (typeof pattern === 'string' && backendMessage.includes(pattern)) {
      return t(key);
    }
    if (pattern instanceof RegExp && pattern.test(backendMessage)) {
      return t(key);
    }
  }
  if (options?.profile) {
    const mapped = getValidationErrorMessage(backendMessage, t);
    if (mapped && mapped !== backendMessage) return mapped;
    return t('errors.api.profileUpdateFailed');
  }
  if (options?.registration) return t('errors.api.registrationFailed');
  return t('errors.api.generic');
}

/** Maps validation.ts English error strings to validation.* i18n keys. */
const VALIDATION_MESSAGE_MAP: Array<{ pattern: string | RegExp; key: string }> = [
  { pattern: 'Email is required', key: 'validation.emailRequired' },
  { pattern: /valid email address/i, key: 'validation.emailInvalid' },
  { pattern: 'Password is required', key: 'validation.passwordRequired' },
  { pattern: /Password must be at least \d+ characters/i, key: 'validation.passwordMinLength' },
  { pattern: /Password must be at most \d+ characters/i, key: 'validation.passwordMaxLength' },
  { pattern: 'Please confirm your password', key: 'validation.confirmPasswordRequired' },
  { pattern: 'Passwords do not match', key: 'validation.passwordsDoNotMatch' },
  { pattern: 'Phone number is required', key: 'validation.phoneRequired' },
  { pattern: /valid phone number/i, key: 'validation.phoneInvalid' },
  { pattern: 'Phone number is too short', key: 'validation.phoneTooShort' },
  { pattern: 'Phone number is too long', key: 'validation.phoneTooLong' },
  { pattern: /complete date of birth/i, key: 'register.dateIncomplete' },
  { pattern: /not valid for the selected month/i, key: 'register.dateDayInvalidForMonth' },
  { pattern: /valid date/i, key: 'validation.dateInvalid' },
  { pattern: /valid postal code/i, key: 'validation.postalCodeInvalid' },
  { pattern: /First name is required/i, key: 'validation.firstNameRequired' },
  { pattern: /Last name is required/i, key: 'validation.lastNameRequired' },
  { pattern: /valid URL/i, key: 'profile.validation.invalidUrl' },
];

/**
 * Returns the i18n key for a validation error string from validation.ts.
 * Use with t(): t(mapValidationErrorToKey(error))
 */
export function mapValidationErrorToKey(validationError: string): string {
  if (!validationError?.trim()) return validationError || '';
  for (const { pattern, key } of VALIDATION_MESSAGE_MAP) {
    if (typeof pattern === 'string' && validationError === pattern) return key;
    if (pattern instanceof RegExp && pattern.test(validationError)) {
      return key;
    }
  }
  return validationError;
}

/** Map a profile-update API error onto specific editor fields when possible. */
export function getProfileFieldErrorsFromApi(
  backendMessage: string,
  t: TranslateFunction
): Record<string, string> {
  const message = backendMessage?.trim() ?? '';
  if (!message) return {};
  const translated = getApiErrorMessage(message, t, { profile: true });
  if (/Professional phone/i.test(message)) return { professionalPhone: translated };
  if (/Professional email/i.test(message)) return { professionalEmail: translated };
  if (/Phone number is invalid/i.test(message) && !/Professional/i.test(message)) {
    return { phone: translated };
  }
  if (/Mobile/i.test(message) && /invalid/i.test(message)) return { mobile: translated };
  if (/Website must be a valid/i.test(message)) return { website: translated };
  if (/cannot be before start year|Study (start|completion) year|study duration/i.test(message)) {
    return { studies: translated };
  }
  return {};
}

/**
 * Returns translated validation error. Use when you have the raw error and t().
 * Handles passwordMinLength by extracting the number for interpolation.
 */
export function getValidationErrorMessage(
  validationError: string,
  t: TranslateFunction
): string {
  const key = mapValidationErrorToKey(validationError);
  if (
    key.startsWith('validation.') ||
    key.startsWith('profile.validation.') ||
    key.startsWith('register.')
  ) {
    const n = validationError.match(/\d+/)?.[0];
    if ((key === 'validation.passwordMinLength' || key === 'validation.passwordMaxLength') && n) {
      return t(key, { n });
    }
    return t(key);
  }
  return validationError;
}
